import { useCallback, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, CalendarPlus, AlarmClock } from 'lucide-react';
import { openLink } from '@tma.js/sdk-react';

import {
  useTournamentIdQuery,
  useAddParticipantsMutation,
  useRemoveParticipantsMutation,
  useSetActiveParticipantMutation,
  useTournamentParticipantsQuery,
} from '@/services/endpoints/tournaments';
import { useTournamentMatchesQuery } from '@/services/endpoints/matches';
import {
  useResolveUsersByEmailQuery,
  useResolveUsersByExternalIdQuery,
} from '@/services/socialApi';
import { Loader } from '@/components/common/Loader';
import { StatusBadge } from '@/components/tournaments/StatusBadge';
import { TournamentPasswordSheet } from '@/components/tournaments/TournamentPasswordSheet';
import { CheerPicker } from '@/components/social/CheerPicker';
import { CoWatchOverlay } from '@/components/social/CoWatchOverlay';
import { PlayerSocialButtons } from '@/components/social/PlayerSocialButtons';
import { useMainButton } from '@/hooks/useMainButton';
import { useHaptics } from '@/hooks/useHaptics';
import { formatDateShort, formatPrizePool } from '@/lib/format';
import { googleCalendarUrl, openAndroidAlarm, androidAlarmUrl } from '@/lib/calendar';
import { useTelegram } from '@/hooks/useTelegram';
import { cdnImage } from '@/lib/cdnImage';
import { cn } from '@/lib/cn';
import { resolvePlayerName } from '@/lib/playerName';
import { EMatchStatus, EParticipantStatus, TOURNAMENT_STATUS } from '@/types/enums';
import type { Participant, TournamentMatches } from '@/types/tournament';

/**
 * Resolve the best Clash-Royale spectator deeplink for a live match. Returns
 * null if metadata is missing or the match isn't currently COLLECTING.
 * Prefers the first_participant's deeplink, falls back to the second's —
 * either gives the same spectator view of the in-progress game.
 */
function getLiveSpectatorUrl(match: TournamentMatches): string | null {
  if (match.match_status !== EMatchStatus.COLLECTING) return null;
  const meta = match.metadata;
  if (!meta) return null;
  const p1 = match.first_participant?.id;
  const p2 = match.second_participant?.id;
  if (p1 && meta[String(p1)]) return meta[String(p1)];
  if (p2 && meta[String(p2)]) return meta[String(p2)];
  return null;
}

type Tab = 'overview' | 'matches' | 'players' | 'rules';

export function TournamentDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const { t } = useTranslation('tournaments');
  const navigate = useNavigate();
  const numericId = Number(id);
  const validId = id && !Number.isNaN(numericId);
  const haptics = useHaptics();

  const [activeTab, setActiveTab] = useState<Tab>('overview');
  // Password prompt for private tournaments (is_private) before registering.
  const [passwordSheetOpen, setPasswordSheetOpen] = useState(false);

  const tournamentQuery = useTournamentIdQuery(numericId, {
    skip: !validId,
    refetchOnMountOrArgChange: true,
    pollingInterval: 8000,
  });
  const tournament = tournamentQuery.data;

  const [register, registration] = useAddParticipantsMutation();
  const [unregister, unregistration] = useRemoveParticipantsMutation();
  const [checkin, checkinMutation] = useSetActiveParticipantMutation();

  // Backend route is `DELETE /tournaments/:tournament/participants/{user}`
  // with Laravel route-model binding on the User model — so we pass the user
  // id (participant.profile.user.id), NOT the participant row id.
  const participantUserId =
    (tournament?.participant?.profile?.user?.id as number | string | undefined) ?? null;

  const onRegister = useCallback(() => {
    if (!validId) return;
    // Private tournaments need a password (secret_code) — prompt first; the
    // sheet's submit (onSubmitPassword) does the actual register.
    if (tournament?.is_private) {
      haptics.selection();
      setPasswordSheetOpen(true);
      return;
    }
    haptics.impact('medium');
    register({ id: String(numericId) })
      .unwrap()
      .then((res) => {
        console.info('[tournament] register OK:', res);
      })
      .catch((err) => {
        console.warn('[tournament] register failed:', err);
      });
  }, [validId, numericId, register, haptics, tournament?.is_private]);

  const onSubmitPassword = useCallback(
    (secretCode: string) => {
      if (!validId) return;
      haptics.impact('medium');
      register({ id: String(numericId), secret_code: secretCode })
        .unwrap()
        .then(() => {
          setPasswordSheetOpen(false);
        })
        .catch((err) => {
          console.warn('[tournament] register (password) failed:', err);
        });
    },
    [validId, numericId, register, haptics],
  );

  const onUnregister = useCallback(() => {
    if (!validId || !participantUserId) return;
    haptics.impact('medium');
    unregister({ id: String(numericId), participantId: String(participantUserId) })
      .unwrap()
      .then(() => console.info('[tournament] unregister OK'))
      .catch((err) => console.warn('[tournament] unregister failed:', err));
  }, [validId, numericId, participantUserId, unregister, haptics]);

  const onCheckin = useCallback(() => {
    if (!validId) return;
    haptics.impact('medium');
    checkin({ id: String(numericId) })
      .unwrap()
      .then(() => console.info('[tournament] checkin OK'))
      .catch((err) => console.warn('[tournament] checkin failed:', err));
  }, [validId, numericId, checkin, haptics]);

  const isOpen =
    tournament?.status === TOURNAMENT_STATUS.REGISTRATION_OPENED ||
    tournament?.status === TOURNAMENT_STATUS.CHECKIN ||
    tournament?.status === TOURNAMENT_STATUS.OPENED;
  const isCheckinPhase = tournament?.status === TOURNAMENT_STATUS.CHECKIN;
  const isFinished =
    tournament?.status === TOURNAMENT_STATUS.FINISHED ||
    tournament?.status === TOURNAMENT_STATUS.CLOSED;
  const isInProgress =
    tournament?.status === TOURNAMENT_STATUS.STARTED ||
    tournament?.status === TOURNAMENT_STATUS.IN_PROGRESS;
  const isJoined = !!tournament?.is_joined;
  // Backend flips participant.status from "registered" → "active" once the
  // user successfully checks in. We treat "active" as the canonical
  // "already checked in" gate (matches mobile-app's MachineStates.CHECKED).
  const isCheckedIn = tournament?.participant?.status === EParticipantStatus.Active;

  // While the tournament is live and we're an active participant, poll for
  // OUR current match so the MainButton can switch to "Play" → MatchDetail,
  // mirroring the mobile-app's "VIEW DETAILS" sticky CTA. Polls only while
  // the conditions hold so we don't burn requests for un-joined viewers.
  const participantId = tournament?.participant?.id ?? null;
  const myMatchesQuery = useTournamentMatchesQuery(
    {
      id: numericId,
      participant: participantId ?? undefined,
    },
    {
      skip: !validId || !participantId || !isInProgress,
      pollingInterval: 2000,
    },
  );
  // First open/pending match — mobile-app uses the same "[0]" indexing.
  const myActiveMatch = useMemo(() => {
    const list = myMatchesQuery.data?.data ?? [];
    const live = list.find((m) =>
      [
        EMatchStatus.OPENED,
        EMatchStatus.WAITING,
        EMatchStatus.CHECKIN,
        EMatchStatus.STARTED,
        EMatchStatus.COLLECTING,
        EMatchStatus.PENDING,
      ].includes(m.match_status as EMatchStatus),
    );
    return live ?? list[0] ?? null;
  }, [myMatchesQuery.data?.data]);

  const onPlayMatch = useCallback(() => {
    if (!myActiveMatch) return;
    haptics.impact('medium');
    navigate(`/matches/${myActiveMatch.id}?t=${numericId}`);
  }, [myActiveMatch, navigate, numericId, haptics]);

  // Check-in takes priority over unregister during the CHECKIN window so the
  // user can actually complete check-in. Once checked in they can no longer
  // unregister (commitment is locked) — mirrors mobile-app behaviour.
  const canCheckin = isJoined && isCheckinPhase && !isCheckedIn;
  const canUnregister = isJoined && isOpen && !isCheckedIn;

  // Once the bracket is live and I have a match, "Play" beats every other
  // CTA — mirrors mobile-app's behaviour where the sticky bottom button
  // becomes the "VIEW DETAILS" / "Gioca" route to MatchDetail.
  const canPlay = isInProgress && isCheckedIn && !!myActiveMatch;

  const mainButtonText = canPlay
    ? t('viewDetails', { defaultValue: 'Play' })
    : isFinished
      ? t('closed', { defaultValue: 'Closed' })
      : isInProgress
        ? t('in_progress', { defaultValue: 'In progress' })
        : canCheckin
          ? t('checkin', { defaultValue: 'Check in' })
          : isJoined && isCheckinPhase && isCheckedIn
            ? t('checkedIn', { defaultValue: 'Checked-in' })
            : canUnregister
              ? t('unregister', { defaultValue: 'Unregister' })
              : isJoined
                ? t('joined', { defaultValue: 'Joined' })
                : isOpen
                  ? t('register', { defaultValue: 'Register' })
                  : t('coming_soon', { defaultValue: 'Coming soon' });

  const mainButtonLoading =
    registration.isLoading || unregistration.isLoading || checkinMutation.isLoading;

  const mainButtonOnClick = canPlay
    ? onPlayMatch
    : canCheckin
      ? onCheckin
      : canUnregister
        ? onUnregister
        : onRegister;

  useMainButton({
    text: mainButtonText,
    enabled:
      !!tournament &&
      !mainButtonLoading &&
      (canPlay || canCheckin || canUnregister || (isOpen && !isJoined)),
    loading: mainButtonLoading,
    onClick: mainButtonOnClick,
  });

  if (tournamentQuery.isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="px-4 py-10 text-center">
        <p className="text-sm text-danger">
          {t('not_found', { defaultValue: 'Tournament not found' })}
        </p>
        <Link to="/tournaments" className="mt-3 inline-block text-sm text-brand">
          ← {t('back_to_list', { defaultValue: 'Back to tournaments' })}
        </Link>
      </div>
    );
  }

  const prize = formatPrizePool(tournament.prize_pool);

  // Pick the most relevant live match to attach the CheerPicker to.
  // Prefer the user's own active match (already polled above), otherwise the
  // first COLLECTING / STARTED match in the bracket. Falls back to null when
  // the tournament is not live yet.
  const liveMatchId = isInProgress ? String(myActiveMatch?.id ?? '') || null : null;

  return (
    <div className="flex flex-col gap-4 pb-24">
      {/* Stessa forma della copertina di un articolo: rientrata, arrotondata,
          col bordo delle card. Due contenuti diversi che si aprono allo stesso
          modo devono APRIRSI allo stesso modo — se un torneo e una notizia
          presentano la loro immagine in due modi, la differenza si legge come
          incoerenza e non come intenzione. */}
      <div className="px-4 pt-3">
        <div className="card-edge aspect-[16/9] w-full overflow-hidden rounded-2xl bg-bg-tertiary">
          {tournament.image && (
            <img
              src={cdnImage(tournament.image)}
              alt=""
              className="h-full w-full object-cover"
              loading="lazy"
            />
          )}
        </div>
      </div>

      {/* Stato e titolo SOTTO l'immagine, non dentro.
          Prima stavano in sovrimpressione su una sfumatura scura, e quella
          sfumatura esisteva solo per renderli leggibili — cioè mangiava un
          terzo della locandina per rimediare a un problema che si crea da sé.
          Fuori, il titolo si legge sempre: su qualunque immagine, chiara,
          scura o affollata. E la locandina si vede tutta. */}
      <div className="-mt-1 flex flex-col gap-2 px-4">
        <StatusBadge status={tournament.status} className="self-start" />
        <h1 className="text-2xl font-bold leading-tight text-fg">{tournament.title}</h1>
      </div>

      <div className="grid grid-cols-3 gap-2 px-4">
        <Stat label={t('players', { defaultValue: 'Players' })}>
          {tournament.participant_count ?? tournament.registered_participant_count ?? 0}/
          {tournament.max_member}
        </Stat>
        <Stat label={t('starts', { defaultValue: 'Starts' })}>
          {formatDateShort(tournament.started_at)}
        </Stat>
        <Stat label={t('prize', { defaultValue: 'Prize' })}>{prize ?? '—'}</Stat>
      </div>

      {/* QSocials co-watch + cheer surfaces, only while the tournament is live. */}
      {isInProgress && <CoWatchOverlay tournamentId={String(numericId)} />}

      {isInProgress && tournament.is_autohandled && (
        <button
          type="button"
          onClick={() => {
            haptics.impact('light');
            navigate(`/tournaments/${numericId}/watch-party`);
          }}
          className="mx-4 flex h-11 items-center justify-center gap-2 rounded-lg bg-danger text-sm font-bold uppercase tracking-wide text-white transition-opacity active:opacity-80"
        >
          <Eye aria-hidden className="h-5 w-5" />
          {t('watch_party', { defaultValue: 'Watch party' })}
        </button>
      )}

      {registration.isError && (
        <div className="mx-4 rounded-md border border-danger/30 bg-danger/10 p-3 text-xs text-danger">
          <p className="font-semibold">Registration failed</p>
          <pre className="mt-1 whitespace-pre-wrap break-words">
            {(() => {
              const err = registration.error as
                { data?: unknown; status?: number; message?: string } | undefined;
              if (!err) return 'Unknown error';
              return JSON.stringify(
                { status: err.status, data: err.data, message: err.message },
                null,
                2,
              ).slice(0, 500);
            })()}
          </pre>
        </div>
      )}
      {registration.isSuccess && (
        <div className="mx-4 rounded-md border border-success/30 bg-success/10 p-3 text-xs text-success">
          ✓ Registered. Reload to see updated player count.
        </div>
      )}
      {unregistration.isError && (
        <div className="mx-4 rounded-md border border-danger/30 bg-danger/10 p-3 text-xs text-danger">
          <p className="font-semibold">Unregister failed</p>
          <pre className="mt-1 whitespace-pre-wrap break-words">
            {(() => {
              const err = unregistration.error as { data?: unknown; status?: number } | undefined;
              return err
                ? JSON.stringify({ status: err.status, data: err.data }, null, 2).slice(0, 400)
                : 'Unknown error';
            })()}
          </pre>
        </div>
      )}
      {checkinMutation.isError && (
        <div className="mx-4 rounded-md border border-danger/30 bg-danger/10 p-3 text-xs text-danger">
          <p className="font-semibold">Check-in failed</p>
          <pre className="mt-1 whitespace-pre-wrap break-words">
            {(() => {
              const err = checkinMutation.error as
                { data?: unknown; status?: number; message?: string } | undefined;
              if (!err) return 'Unknown error';
              return JSON.stringify(
                { status: err.status, data: err.data, message: err.message },
                null,
                2,
              ).slice(0, 500);
            })()}
          </pre>
        </div>
      )}

      {canCheckin && (
        <button
          type="button"
          onClick={onUnregister}
          disabled={unregistration.isLoading}
          className="mx-4 flex h-10 items-center justify-center rounded-lg border border-border bg-bg-secondary text-sm font-medium text-fg-muted transition-opacity active:opacity-80 disabled:opacity-50"
        >
          {t('leave_tournament', { defaultValue: 'Leave tournament' })}
        </button>
      )}

      <div role="tablist" className="flex gap-1 border-b border-border px-4">
        {(['overview', 'matches', 'players', 'rules'] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={activeTab === tab}
            onClick={() => setActiveTab(tab)}
            className={cn(
              'flex-1 border-b-2 py-3 text-sm font-medium transition-colors',
              activeTab === tab ? 'border-brand text-fg' : 'border-transparent text-fg-muted',
            )}
          >
            {t(`tab.${tab}`, { defaultValue: tab.charAt(0).toUpperCase() + tab.slice(1) })}
          </button>
        ))}
      </div>

      <div className="px-4">
        {activeTab === 'overview' && <Overview tournament={tournament} />}
        {activeTab === 'matches' && <Matches tournamentId={numericId} />}
        {activeTab === 'players' && <Players tournamentId={numericId} />}
        {activeTab === 'rules' && (
          <p className="py-8 text-center text-sm text-fg-muted">
            {t('rules_coming', { defaultValue: 'Rules viewer coming next.' })}
          </p>
        )}
      </div>

      {/* Cheer the live match. Stays out of the way for non-joined viewers
          (no liveMatchId) and during pre-bracket states. */}
      {liveMatchId && <CheerPicker matchId={liveMatchId} />}

      {tournament.is_private && passwordSheetOpen && (
        <TournamentPasswordSheet
          open
          busy={registration.isLoading}
          error={
            registration.isError
              ? t('register_failed_password', {
                  defaultValue: 'Registration failed. Check the password and try again.',
                })
              : null
          }
          onSubmit={onSubmitPassword}
          onClose={() => setPasswordSheetOpen(false)}
        />
      )}
    </div>
  );
}

function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="card-edge rounded-lg bg-bg-secondary p-3 text-center">
      <p className="text-[10px] font-medium uppercase tracking-wide text-fg-muted">{label}</p>
      <p className="mt-1 text-sm font-semibold text-fg">{children}</p>
    </div>
  );
}

function Overview({
  tournament,
}: {
  tournament: NonNullable<ReturnType<typeof useTournamentIdQuery>['data']>;
}) {
  const { t } = useTranslation('tournaments');
  const haptics = useHaptics();
  const { platform } = useTelegram();

  const startsAt = tournament.started_at;
  const calendarEvent = startsAt
    ? {
        title: tournament.title,
        start: startsAt,
        end: tournament.end_at,
        description: tournament.description ?? undefined,
      }
    : null;
  // Native alarm only exists on Android (iOS exposes none). Best-effort inside
  // the Telegram WebView via the SET_ALARM intent.
  const canSetAlarm =
    platform === 'android' &&
    !!startsAt &&
    !!androidAlarmUrl({ title: tournament.title, start: startsAt });

  const onAddToCalendar = () => {
    if (!calendarEvent) return;
    const url = googleCalendarUrl(calendarEvent);
    if (!url) return;
    haptics.impact('light');
    // openLink hands the https URL to Telegram → opens Google Calendar's
    // prefilled new-event sheet externally. (A .ics blob just renders as text
    // inside the iOS WebView, so we don't use it here.)
    openLink.ifAvailable(url);
  };
  const onSetAlarm = () => {
    if (!startsAt) return;
    haptics.impact('medium');
    openAndroidAlarm({ title: tournament.title, start: startsAt });
  };

  return (
    <div className="flex flex-col gap-4 py-4">
      {calendarEvent && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onAddToCalendar}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-bg-secondary px-3 py-2.5 text-sm font-semibold text-fg transition-opacity active:opacity-80"
          >
            <CalendarPlus className="h-4 w-4 text-brand" />
            {t('reminders.calendar', { defaultValue: 'Add to calendar' })}
          </button>
          {canSetAlarm && (
            <button
              type="button"
              onClick={onSetAlarm}
              className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-bg-secondary px-3 py-2.5 text-sm font-semibold text-fg transition-opacity active:opacity-80"
            >
              <AlarmClock className="h-4 w-4 text-brand" />
              {t('reminders.alarm', { defaultValue: 'Set alarm' })}
            </button>
          )}
        </div>
      )}

      {tournament.description && (
        <section>
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-fg-muted">
            About
          </h2>
          <p className="whitespace-pre-line text-sm leading-relaxed text-fg">
            {tournament.description}
          </p>
        </section>
      )}

      <section className="card-edge rounded-xl bg-bg-secondary p-4">
        <dl className="grid grid-cols-2 gap-y-3 text-sm">
          <Detail label="Mode">
            {tournament.team_mates > 1 ? `Team of ${tournament.team_mates}` : 'Solo'}
          </Detail>
          <Detail label="Region">{tournament.region?.title ?? '—'}</Detail>
          <Detail label="Starts">{formatDateShort(tournament.started_at)}</Detail>
          <Detail label="Ends">{formatDateShort(tournament.end_at)}</Detail>
        </dl>
      </section>
    </div>
  );
}

function Players({ tournamentId }: { tournamentId: number }) {
  const { t } = useTranslation('tournaments');
  const { data, isLoading } = useTournamentParticipantsQuery(tournamentId, {
    refetchOnMountOrArgChange: true,
  });
  const participants = useMemo(() => data?.data ?? [], [data?.data]);

  // Identity bridge — resolve each participant to their Logto `sub` so the
  // follow/add-friend buttons can fire against real users.
  //
  // PRIMARY (deterministic): resolve by the participant's Laravel `users.id`
  // (`profile.user.id`), which every user self-publishes as `externalUserId`
  // on launch (useProfileSync). This is immune to email mismatches from Apple
  // "Hide My Email" / social login.
  //
  // FALLBACK: only for participants the id-resolve didn't match, resolve by
  // their login email (`profile.user.email`). Unmatched on both paths → no
  // buttons. The id-based result always wins.

  // Dedup Laravel ids (digits-only) across the page.
  const externalIds = useMemo(() => {
    const set = new Set<string>();
    for (const p of participants) {
      const uid = p.profile?.user?.id;
      if (uid != null) set.add(String(uid));
    }
    return Array.from(set);
  }, [participants]);

  const { data: resolvedById } = useResolveUsersByExternalIdQuery(
    { externalIds },
    { skip: externalIds.length === 0 },
  );

  // Laravel id (string) → Logto sub.
  const subByExternalId = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of resolvedById ?? []) map.set(r.externalUserId, r.userId);
    return map;
  }, [resolvedById]);

  // Emails to fall back on — ONLY for participants whose id didn't resolve.
  // Avoids firing the email resolver for ids already covered.
  const fallbackEmails = useMemo(() => {
    const set = new Set<string>();
    for (const p of participants) {
      const uid = p.profile?.user?.id;
      if (uid != null && subByExternalId.has(String(uid))) continue;
      const email = p.profile?.user?.email;
      if (email) set.add(email);
    }
    return Array.from(set);
  }, [participants, subByExternalId]);

  const { data: resolvedByEmail } = useResolveUsersByEmailQuery(
    { emails: fallbackEmails },
    { skip: fallbackEmails.length === 0 },
  );

  // email → Logto sub (fallback path only).
  const subByEmail = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of resolvedByEmail ?? []) map.set(r.email, r.userId);
    return map;
  }, [resolvedByEmail]);

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Loader />
      </div>
    );
  }
  if (participants.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-fg-muted">
        {t('no_participants', { defaultValue: 'No players registered yet.' })}
      </p>
    );
  }
  return (
    <ul className="flex flex-col gap-2 py-4">
      {participants.map((p) => {
        const uid = p.profile?.user?.id;
        const email = p.profile?.user?.email;
        // Prefer the deterministic id-based result; only fall back to email.
        const sub =
          (uid != null ? subByExternalId.get(String(uid)) : undefined) ??
          (email ? subByEmail.get(email) : undefined);
        return <ParticipantRow key={p.id} participant={p} socialUserId={sub} />;
      })}
    </ul>
  );
}

function ParticipantRow({
  participant,
  socialUserId,
}: {
  participant: Participant;
  /**
   * The participant's Logto `sub`, resolved by the parent batch-resolve from
   * their email. `undefined` when the email didn't resolve (or there was no
   * email) — in that case the social buttons are not rendered.
   */
  socialUserId?: string;
}) {
  const { t } = useTranslation('tournaments');
  const name = resolvePlayerName(participant) ?? t('unknown_player', { defaultValue: 'Player' });
  const avatar = participant.profile?.user?.avatar ?? null;
  const initial = name.charAt(0).toUpperCase();
  return (
    <li className="flex items-center gap-3 rounded-xl bg-bg-secondary p-3">
      <div className="h-10 w-10 shrink-0 overflow-hidden rounded-full bg-bg-tertiary">
        {avatar ? (
          <img src={avatar} alt="" className="h-full w-full object-cover" loading="lazy" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-sm font-semibold text-fg">
            {initial}
          </div>
        )}
      </div>
      <p className="min-w-0 flex-1 truncate text-sm font-semibold text-fg">{name}</p>
      {socialUserId && <PlayerSocialButtons userId={socialUserId} interactive />}
    </li>
  );
}

// Same trap as WatchParty: ProfileInfoResource on the backend returns the
// literal string "unknown" when info.nickname is null, so falling back via
// `?? 'TBD'` never fires. Skip "unknown" and fall through to the QLASH user
// record's nickname / name.
function resolveNickname(
  participant: TournamentMatches['first_participant'] | TournamentMatches['second_participant'],
): string {
  if (!participant) return 'TBD';
  const infoNick = participant.profile?.info?.nickname;
  if (infoNick && infoNick !== 'unknown') return infoNick;
  const user = participant.profile?.user as
    { nickname?: string; name?: string; username?: string } | undefined;
  return user?.nickname || user?.username || user?.name || 'TBD';
}

function Matches({ tournamentId }: { tournamentId: number }) {
  const { data, isLoading } = useTournamentMatchesQuery(
    { id: tournamentId },
    { refetchOnMountOrArgChange: true, pollingInterval: 15000 },
  );
  const [view, setView] = useState<'list' | 'bracket'>('list');

  const grouped = useMemo(() => {
    const byRound = new Map<number, TournamentMatches[]>();
    (data?.data ?? []).forEach((m) => {
      const round = m.round || 1;
      if (!byRound.has(round)) byRound.set(round, []);
      byRound.get(round)!.push(m);
    });
    return Array.from(byRound.entries()).sort(([a], [b]) => a - b);
  }, [data]);

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (grouped.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-fg-muted">
        Matches will appear once the tournament starts.
      </p>
    );
  }

  const totalRounds = grouped.length;

  return (
    <div className="flex flex-col gap-3 py-2">
      <div className="inline-flex self-start rounded-full bg-bg-secondary p-0.5">
        {(['list', 'bracket'] as const).map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setView(v)}
            className={cn(
              'rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide transition-colors',
              view === v ? 'bg-brand text-brand-fg' : 'text-fg-muted',
            )}
          >
            {v}
          </button>
        ))}
      </div>

      {view === 'list' && (
        <div className="flex flex-col gap-4">
          {grouped.map(([round, items]) => (
            <section key={round}>
              <h3 className="mb-2 text-[10px] font-bold uppercase tracking-wider text-fg-muted">
                {round === totalRounds && totalRounds > 1
                  ? 'Final'
                  : round === totalRounds - 1 && totalRounds > 2
                    ? 'Semi-final'
                    : `Round ${round}`}
              </h3>
              <ul className="flex flex-col gap-2">
                {items.map((match) => (
                  <MatchListRow key={match.id} match={match} />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      {view === 'bracket' && <BracketView rounds={grouped} />}
    </div>
  );
}

function MatchListRow({ match }: { match: TournamentMatches }) {
  const p1 = resolveNickname(match.first_participant);
  const p2 = resolveNickname(match.second_participant);
  return (
    <Link
      to={`/matches/${match.id}`}
      className="flex items-center justify-between gap-3 rounded-lg bg-bg-secondary p-3 transition-opacity active:opacity-80"
    >
      <div className="min-w-0 text-sm">
        <p className="truncate font-medium text-fg">
          {p1}
          <span className="mx-2 text-fg-muted">vs</span>
          {p2}
        </p>
      </div>
      <StatusBadge status={match.match_status} />
    </Link>
  );
}

function BracketView({ rounds }: { rounds: Array<[number, TournamentMatches[]]> }) {
  return (
    // Horizontal scroll with one column per round; matches space-around inside
    // their column so they line up with their parent match in the next round.
    // No SVG connector lines — just visual proximity (mirrors mobile-app's
    // approach of relying on flex spacing rather than drawn brackets).
    <div className="-mx-4 overflow-x-auto px-4 pb-2">
      <div className="flex min-w-max gap-3">
        {rounds.map(([round, items], idx) => (
          <div key={round} className="flex min-w-[160px] flex-col">
            <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-fg-muted">
              {idx === rounds.length - 1 && rounds.length > 1
                ? 'Final'
                : idx === rounds.length - 2 && rounds.length > 2
                  ? 'Semi'
                  : `R${round}`}
            </div>
            <div className="flex flex-1 flex-col justify-around gap-2">
              {items.map((match) => (
                <BracketCard key={match.id} match={match} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BracketCard({ match }: { match: TournamentMatches }) {
  const p1 = resolveNickname(match.first_participant);
  const p2 = resolveNickname(match.second_participant);
  const winnerId = match.winner_id;
  const isWinner1 = winnerId && match.first_participant?.id === winnerId;
  const isWinner2 = winnerId && match.second_participant?.id === winnerId;
  // Only show the watch-live eye when the match is *actually* being played
  // right now AND we have a deeplink to hand off to Clash Royale. A
  // CHECKIN / WAITING / FINISHED match has nothing to spectate.
  const liveUrl = getLiveSpectatorUrl(match);

  const onWatch = (e: React.MouseEvent): void => {
    // Stop the outer Link from also navigating to the match detail page.
    e.preventDefault();
    e.stopPropagation();
    if (!liveUrl) return;
    // Same HTTPS-redirect bridge as WatchParty.tsx — Telegram's openLink
    // rejects non-http(s) schemes, so we bounce through a static page on
    // our own origin that does location.replace(clashroyale://…).
    const redirect = `${window.location.origin}/watch-redirect.html?to=${encodeURIComponent(liveUrl)}`;
    openLink.ifAvailable(redirect);
  };

  return (
    <Link
      to={`/matches/${match.id}`}
      className="relative flex flex-col overflow-hidden rounded-lg bg-bg-secondary text-xs transition-opacity active:opacity-80"
    >
      <div
        className={cn(
          'flex items-center justify-between gap-2 border-b border-border px-2 py-1.5',
          isWinner1 && 'text-brand',
          isWinner2 && 'text-fg-muted opacity-60',
        )}
      >
        <span className="truncate">{p1}</span>
        {isWinner1 && <span aria-hidden>★</span>}
      </div>
      <div
        className={cn(
          'flex items-center justify-between gap-2 px-2 py-1.5',
          isWinner2 && 'text-brand',
          isWinner1 && 'text-fg-muted opacity-60',
        )}
      >
        <span className="truncate">{p2}</span>
        {isWinner2 && <span aria-hidden>★</span>}
      </div>
      {liveUrl && (
        <button
          type="button"
          onClick={onWatch}
          aria-label="Watch live match"
          className={cn(
            'absolute right-1 top-1 flex h-6 w-6 items-center justify-center',
            'rounded-full bg-danger text-white shadow-[0_0_8px_rgba(237,48,76,0.6)]',
            'animate-pulse transition-opacity active:opacity-80',
          )}
        >
          <Eye aria-hidden className="h-3 w-3" />
        </button>
      )}
    </Link>
  );
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-fg-muted">{label}</dt>
      <dd className="mt-0.5 text-fg">{children}</dd>
    </div>
  );
}
